/**
 * /conta — dados do usuário logado + troca da própria senha.
 *
 * F1 do redesenho (CEO 2026-09-23): primeira tela no vocabulário de
 * formulário (`components/form/FormKit`) — coluna centrada, ícone + título,
 * rótulo com etiqueta de obrigatório, caixa de dica e barra de ações no
 * rodapé. Antes usava Tailwind cru de fundo escuro (`bg-neutral-900`), que
 * no tema claro — o padrão do ERP — saía lavado.
 */

import { useState } from "react";
import { trpc } from "../lib/trpc.js";
import { useAuth } from "../hooks/useAuth.js";
import { PasswordInput } from "../components/common/PasswordInput.js";
import { Field, FormPage, FormSection, Hint, PageActions } from "../components/form/FormKit.js";
import { ErpShell } from "../components/layout/ErpShell.js";
import { useT } from "../i18n/index.js";

export default function Conta() {
  const { user } = useAuth();
  const t = useT();
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [localError, setLocalError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  const changePassword = trpc.auth.changePassword.useMutation({
    onSuccess: () => {
      setDone(true);
      setCurrent("");
      setNext("");
      setConfirm("");
      setLocalError(null);
    },
    onError: () => setDone(false),
  });

  function submit() {
    setLocalError(null);
    setDone(false);
    if (next.length < 8) {
      setLocalError(t("account.errorMinLength"));
      return;
    }
    if (next !== confirm) {
      setLocalError(t("account.errorMismatch"));
      return;
    }
    if (next === current) {
      setLocalError(t("account.errorSameAsCurrent"));
      return;
    }
    changePassword.mutate({ currentPassword: current, newPassword: next });
  }

  const erro = localError ?? (changePassword.error?.message || null);
  const mensagem = erro
    ? ({ tone: "err", text: erro } as const)
    : done
      ? ({ tone: "ok", text: `✓ ${t("account.successChanged")}` } as const)
      : null;

  return (
    <ErpShell>
      <FormPage
        icon={
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="12" cy="8" r="4" />
            <path d="M4 21v-1a6 6 0 0 1 6-6h4a6 6 0 0 1 6 6v1" />
          </svg>
        }
        title={t("account.title")}
        subtitle={t("account.pageSubtitle")}
      >
        <FormSection title={t("account.userLabel")}>
          <Field label={t("account.emailLabel")}>
            <input className="fin" value={user?.email ?? "—"} disabled readOnly />
          </Field>
          <Hint>{t("account.emailHint")}</Hint>
        </FormSection>

        <FormSection title={t("account.changePassword")}>
          <Field label={t("account.currentPassword")} required>
            <PasswordInput
              autoComplete="current-password"
              value={current}
              onChange={(e) => setCurrent(e.target.value)}
              className="fin"
            />
          </Field>
          <Field label={t("account.newPassword")} required help={t("account.minHint")}>
            <PasswordInput
              autoComplete="new-password"
              value={next}
              onChange={(e) => setNext(e.target.value)}
              className="fin"
            />
          </Field>
          <Field label={t("account.confirmNewPassword")} required>
            <PasswordInput
              autoComplete="new-password"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              className="fin"
            />
          </Field>
        </FormSection>

        <PageActions
          saveLabel={changePassword.isPending ? t("account.saving") : t("account.changePassword")}
          onSave={submit}
          saving={changePassword.isPending}
          disabled={!current || !next || !confirm}
          message={mensagem}
        />
      </FormPage>
    </ErpShell>
  );
}
